// ═══════════════════════════════════════════════════════════════
// useSignedSrc.js — Hook React pour signer dynamiquement une URL asset
// Session Greg NEXT SUITE — 22 avril 2026
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { signUrl, getCachedSignedUrl } from '../services/signedUrlApi';

function initialValue(path) {
    if (!path) return null;
    if (!path.startsWith('/api/')) return path; // URL externe, pas de signature
    return getCachedSignedUrl(path); // lookup sync du cache, évite le flash vide au remount
}

/**
 * Retourne une URL signée pour un path `/api/...`. Si déjà en cache frais,
 * valeur retournée synchrone dès le 1er render (évite "l'image qui apparaît
 * puis disparaît" quand un parent re-render démonte/remonte l'arbre).
 */
export default function useSignedSrc(path) {
    const [signed, setSigned] = useState(() => initialValue(path));

    useEffect(() => {
        if (!path) {
            setSigned(null);
            return undefined;
        }
        if (!path.startsWith('/api/')) {
            setSigned(path);
            return undefined;
        }
        // Si cache hit sync, on synchronise l'état sans re-fetch
        const cached = getCachedSignedUrl(path);
        if (cached) {
            setSigned((prev) => (prev === cached ? prev : cached));
            return undefined;
        }
        let cancelled = false;
        signUrl(path)
            .then((url) => {
                if (!cancelled) setSigned(url);
            })
            .catch(() => {
                if (!cancelled) setSigned(null);
            });
        return () => {
            cancelled = true;
        };
    }, [path]);

    return signed;
}
